import React from "react";
import { Link } from "react-router-dom";
import { Wordmark } from "@/components/landing/Wordmark";
import { ScreenplayPattern } from "@/components/landing/ScreenplayPattern";

const SOCIAL = [
  {
    name: "Instagram",
    href: "https://instagram.com/voxiumscript",
    ariaLabel: "Voxium Script on Instagram",
    testId: "footer-social-instagram",
  },
  {
    name: "Facebook",
    href: "https://www.facebook.com/profile.php?id=61589900738949",
    ariaLabel: "Voxium Script on Facebook",
    testId: "footer-social-facebook",
  },
];

/**
 * Voxium Script footer. Rendered on every route (landing, /privacy, /terms).
 *
 * Rows, top to bottom:
 *   1. wordmark
 *   2. social links (Instagram · Facebook)
 *   3. legal links (Privacy · Terms) — required on every view
 *   4. gold hairline
 *   5. copyright
 */
export const SiteFooter = () => {
  return (
    <footer
      className="relative w-full bg-ink overflow-hidden pt-14 sm:pt-20 pb-9 sm:pb-12"
      data-testid="site-footer"
    >
      {/* Screenplay-pattern texture — same component as hero, same 12% opacity */}
      <div className="absolute inset-0 pointer-events-none select-none">
        <ScreenplayPattern className="w-full h-full" />
      </div>

      <div className="relative z-10 max-w-[78rem] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="flex flex-col items-center text-center gap-y-5 sm:gap-y-6">

          {/* Row 1 — wordmark (cream "voxium" + gold "script") */}
          <Wordmark tone="dark" size="footer" testId="footer-wordmark" />

          {/* Row 2 — social links: Instagram · Facebook */}
          <nav
            aria-label="Voxium Script social links"
            className="font-ui text-creamtext text-base"
            data-testid="footer-social-nav"
          >
            <a
              href={SOCIAL[0].href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={SOCIAL[0].ariaLabel}
              className="text-creamtext hover:text-gold transition-colors duration-200 focus-visible:outline-2 focus-visible:outline focus-visible:outline-gold focus-visible:outline-offset-4 rounded-sm tap-extend"
              data-testid={SOCIAL[0].testId}
            >
              {SOCIAL[0].name}
            </a>
            <span aria-hidden="true" className="mx-3 text-creamtext/40">·</span>
            <a
              href={SOCIAL[1].href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={SOCIAL[1].ariaLabel}
              className="text-creamtext hover:text-gold transition-colors duration-200 focus-visible:outline-2 focus-visible:outline focus-visible:outline-gold focus-visible:outline-offset-4 rounded-sm tap-extend"
              data-testid={SOCIAL[1].testId}
            >
              {SOCIAL[1].name}
            </a>
          </nav>

          {/* Row 3 — legal links: Privacy · Terms · Refunds (required on every view) */}
          <nav
            aria-label="Voxium Script legal"
            className="font-ui text-creamtext/85 text-sm"
            data-testid="footer-legal-nav"
          >
            <Link
              to="/privacy"
              className="text-creamtext/85 hover:text-gold transition-colors duration-200 focus-visible:outline-2 focus-visible:outline focus-visible:outline-gold focus-visible:outline-offset-4 rounded-sm tap-extend"
              data-testid="footer-privacy-link"
            >
              Privacy
            </Link>
            <span aria-hidden="true" className="mx-3 text-creamtext/40">·</span>
            <Link
              to="/terms"
              className="text-creamtext/85 hover:text-gold transition-colors duration-200 focus-visible:outline-2 focus-visible:outline focus-visible:outline-gold focus-visible:outline-offset-4 rounded-sm tap-extend"
              data-testid="footer-terms-link"
            >
              Terms
            </Link>
            <span aria-hidden="true" className="mx-3 text-creamtext/40">·</span>
            <Link
              to="/refunds"
              className="text-creamtext/85 hover:text-gold transition-colors duration-200 focus-visible:outline-2 focus-visible:outline focus-visible:outline-gold focus-visible:outline-offset-4 rounded-sm tap-extend"
              data-testid="footer-refunds-link"
            >
              Refunds
            </Link>
          </nav>

          {/* Hairline ornament — gold @ 40%, 80px wide, centered.
              Echoes the pitch-pricing block's ornament language. */}
          <div
            className="w-20 h-px bg-gold/40"
            aria-hidden="true"
            data-testid="footer-hairline"
          />

          {/* Row 5 — copyright */}
          <p
            className="font-ui text-[13px] text-creamtext/60 tracking-wide"
            data-testid="footer-copyright"
          >
            © 2026 RayMar &amp; Co Enterprises LLC
          </p>

        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
